import { useState } from "react";
import dayjs from "dayjs";
import { Button, Popover, Typography as T, Box, GlobalStyles } from "@mui/material";
import { CalendarToday } from "@mui/icons-material";
import { DateRangePicker, DefinedRange, DateRange } from "date-range-picker-mui";
import { updateDateRange } from "./FilterFunctions";
import { calendarDefaults } from "../../constants/calendarDefaults";
import { useFilterContext } from "../../contexts/FilterProvider";
import useLocalFilterState from "./useLocalFilterState";
import { DEFAULT_START_DATE, DEFAULT_END_DATE } from "../../constants/defaultFilterState";

const LONG_DATE_FORMAT = "DD MMM YYYY";
const FILTER_DATE_FORMAT = "YYYY-MM-DD";
const MIN_START_DATE = dayjs("2020-01-01 00:00");

// Workaround: create CSS rules to hide years in the year dropdown that are outside the valid date range
// The date-range-picker-mui library does not provide a built-in way to limit the years shown in the year dropdown
// For future maintainers: if the library is updated to support this feature, the getYearDropdownStyles function and its usage
// can be removed and replaced by the library's built-in functionality for limiting year options.
const getYearDropdownStyles = () => {
  const minYear = MIN_START_DATE.year();
  const currentYear = dayjs().year();

  const validYears = Array.from({ length: currentYear - minYear + 1 }, (_, i) => minYear + i);
  const validMonths = Array.from({ length: 12 }, (_, i) => i);
  const whitelistSelectors = [...validYears, ...validMonths].map((val) => `:not([data-value="${val}"])`).join("");

  // Define scope to only apply rules within the date range filter popover
  const scope = ".date-range-filter-popover";

  return {
    // Hide years outside the valid range
    [`${scope} li.MuiMenuItem-root[data-value]${whitelistSelectors}`]: {
      display: "none",
    },
  };
};

type DynamicDateRange = {
  label?: string;
} & DateRange;

const generateDateRangeFromLabel = (label: string) => {
  const foundShortcut = calendarDefaults.find(({ label: shortcutLabel }) => shortcutLabel === label);
  return foundShortcut ?? undefined;
};

const DateRangeFilter = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { filterState, applyFilters } = useFilterContext();
  const { transientFilterState, setTransientFilterState } = useLocalFilterState(filterState);

  if (!filterState) return <div />;

  const { dates, dynamic_date_range } = filterState;

  const { start, end } = { start: dates.start ? dayjs(dates.start) : null, end: dates.end ? dayjs(dates.end) : null };

  // using Dayjs due to its ease of date math and parsing/stringifying dates;
  // we need to convert it to the native Date object to work.
  let initialDateRange: DefinedRange | undefined =
    !dynamic_date_range && start?.isValid() && end?.isValid()
      ? {
          startDate: start.toDate(),
          endDate: end.toDate(),
          label: "",
        }
      : dynamic_date_range
        ? generateDateRangeFromLabel(dynamic_date_range)
        : undefined;

  // Display the initial applied date range (Last Year) when no date filter is selected/applied
  if (initialDateRange === undefined) {
    initialDateRange = {
      startDate: DEFAULT_START_DATE.toDate(),
      endDate: DEFAULT_END_DATE.toDate(),
      label: "",
    };
  }

  const buttonText =
    start?.isValid() && end?.isValid()
      ? `${start.format(LONG_DATE_FORMAT)}${!start.isSame(end) ? ` — ${end.format(LONG_DATE_FORMAT)}` : ""}`
      : `${DEFAULT_START_DATE.format(LONG_DATE_FORMAT)} — ${DEFAULT_END_DATE.format(LONG_DATE_FORMAT)}`;

  const handleChange = (range: DynamicDateRange) => {
    const newState = updateDateRange(
      transientFilterState,
      dayjs(range.startDate).format(FILTER_DATE_FORMAT),
      dayjs(range.endDate).format(FILTER_DATE_FORMAT),
      range.label ?? null,
    );
    setTransientFilterState(newState);
    applyFilters(newState);
    setAnchorEl(null);
  };

  return (
    <div>
      <GlobalStyles styles={getYearDropdownStyles()} />
      <Button
        fullWidth
        variant="outlined"
        startIcon={<CalendarToday />}
        sx={{
          padding: "5px 8px",
          color: start?.isValid() ? "black" : "grey.500",
          "&:hover": {
            color: start?.isValid() ? "black" : "grey.500",
          },
          "& .MuiButton-startIcon": {
            marginRight: "12px !important",
          },
        }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {buttonText}
      </Button>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        className="date-range-filter-popover"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box
          sx={{
            marginBottom: 2,
            "& .MuiPaper-root": { boxShadow: "none" },
          }}
        >
          <DateRangePicker
            open={!!anchorEl}
            toggle={() => setAnchorEl(null)}
            initialDateRange={initialDateRange}
            definedRanges={calendarDefaults}
            onChange={handleChange}
            minDate={MIN_START_DATE.toDate()}
            maxDate={DEFAULT_END_DATE.toDate()}
          />
          <T textAlign="center" fontStyle="italic">
            Date range must be between {MIN_START_DATE.format(LONG_DATE_FORMAT)} and today.
          </T>
        </Box>
      </Popover>
    </div>
  );
};

export default DateRangeFilter;
